import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import type { RoomItemState } from '../../types/subprojects';
import { Checkbox } from '../ui/checkbox';
import { useAppSettings } from '../../hook/useAppSettings';

type DecimalString = string;

type SupplierCommercialMeta = {
  desvio_comercial_pct?: string;
  beneficio_comercial_pct?: string;
};

export type AutocompleteOption = { label: string; value: string; meta?: SupplierCommercialMeta };

const EMPTY_VALUES = {
  roomName: '',
  ref: '',
  supplier: '',
  supplierId: '',
  description: '',
  category: '',
  categoryId: '',
  quantity: 1,
  precio_tarifa: '' as DecimalString,
  desvio_comercial_pct: '0' as DecimalString,
  beneficio_comercial_pct: '0' as DecimalString,
  montaje_pct: '0' as DecimalString,
  discount: '' as DecimalString,
  real_cost: '' as DecimalString,
  state: 'pendiente' as RoomItemState,
  notes: '',
  customRealBudget: false,
  desvio_personalizado: false,
  beneficio_personalizado: false,
};

const STATE_OPTIONS: Array<{ label: string; value: RoomItemState }> = [
  { label: 'Pendiente', value: 'pendiente' },
  { label: 'Pedido', value: 'pedido' },
  { label: 'Recibido', value: 'recibido' },
  { label: 'Instalado', value: 'instalado' },
];

export type ItemFormValues = typeof EMPTY_VALUES;

export interface ItemFormDialogProps {
  open: boolean;
  initialValues?: Partial<ItemFormValues>;
  isSubmitting?: boolean;
  onSubmit: (values: ItemFormValues) => Promise<void> | void;
  onClose: () => void;
  roomOptions?: AutocompleteOption[];
  categoryOptions?: AutocompleteOption[];
  categoriesLoading?: boolean;
  supplierOptions?: AutocompleteOption[];
  suppliersLoading?: boolean;
}

const normalizeDecimal = (value: DecimalString, allowEmpty = false) => {
  if (value === '' || value === undefined || value === null) {
    return allowEmpty ? '' : '0';
  }
  const numeric = Number.parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(numeric) ? numeric.toFixed(2) : '0';
};

export function ItemFormDialog({
  open,
  initialValues,
  isSubmitting = false,
  onSubmit,
  onClose,
  roomOptions = [],
  categoryOptions = [],
  categoriesLoading,
  supplierOptions = [],
  suppliersLoading,
}: ItemFormDialogProps) {
  const { applyIva } = useAppSettings();
  const computedInitialValues = useMemo(
    () => ({ ...EMPTY_VALUES, ...initialValues }),
    [initialValues],
  );

  const form = useForm<ItemFormValues>({
    defaultValues: computedInitialValues,
  });
  const customRealBudgetEnabled = form.watch('customRealBudget');
  const quantityValue = form.watch('quantity');
  const precioTarifaValue = form.watch('precio_tarifa');
  const desvioComercialValue = form.watch('desvio_comercial_pct');
  const beneficioComercialValue = form.watch('beneficio_comercial_pct');
  const montajePctValue = form.watch('montaje_pct');
  const discountValue = form.watch('discount');
  const desvioPersonalizadoEnabled = form.watch('desvio_personalizado');
  const beneficioPersonalizadoEnabled = form.watch('beneficio_personalizado');

  const parseNum = (value: string | number | null | undefined) => {
    const parsed = Number.parseFloat(String(value ?? '0').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const precioTarifa = parseNum(precioTarifaValue);
  const desvioComercialPct = parseNum(desvioComercialValue);
  const beneficioComercialPct = parseNum(beneficioComercialValue);
  const montajePct = parseNum(montajePctValue);
  const discountPct = parseNum(discountValue);
  const unitCostCalculated = precioTarifa * (1 - desvioComercialPct / 100);
  const pvpCalculated =
    precioTarifa * (1 + beneficioComercialPct / 100) * (1 + montajePct / 100);
  const costTotalCalculated = unitCostCalculated * (quantityValue || 0);
  const mountingAmountCalculated = costTotalCalculated * (montajePct / 100);
  const pvpTotalCalculated = pvpCalculated * (quantityValue || 0) * (1 - discountPct / 100);
  const pvpIvaCalculated = applyIva(pvpTotalCalculated);
  const marginTotalCalculated = pvpTotalCalculated - costTotalCalculated - mountingAmountCalculated;
  const realCostInputValue = parseNum(form.watch('real_cost'));
  const effectiveRealCost = customRealBudgetEnabled ? realCostInputValue : costTotalCalculated;
  const desvioCosteCalculated = costTotalCalculated - effectiveRealCost;

  useEffect(() => {
    if (!customRealBudgetEnabled) {
      form.setValue('real_cost', costTotalCalculated.toFixed(2), { shouldDirty: false });
    }
  }, [costTotalCalculated, customRealBudgetEnabled, form]);

  useEffect(() => {
    if (open) {
      form.reset(computedInitialValues);
    }
  }, [open, computedInitialValues, form]);

  const stateOptions = useMemo(() => STATE_OPTIONS, []);

  const handleSubmit = form.handleSubmit(async (values) => {
    const payload: ItemFormValues = {
      ...values,
      quantity: Number(values.quantity) || 0,
      precio_tarifa: normalizeDecimal(values.precio_tarifa),
      desvio_comercial_pct: normalizeDecimal(values.desvio_comercial_pct),
      beneficio_comercial_pct: normalizeDecimal(values.beneficio_comercial_pct),
      montaje_pct: normalizeDecimal(values.montaje_pct),
      discount: normalizeDecimal(values.discount),
      real_cost: normalizeDecimal(values.real_cost, true),
    };
    await onSubmit(payload);
  });

  const personalizableFieldClassName = 'space-y-2';
  const dialogContentClassName = 'max-h-[94vh] overflow-y-auto';
  const dialogContentStyle = { width: 'calc(100vw - 2rem)', maxWidth: '43rem' };

  const AutocompleteField = ({
    label,
    placeholder,
    value,
    onChange,
    options,
    disabled,
    loading,
    allowCustom = true,
    onSelect,
  }: {
    label: string;
    placeholder: string;
    value: string;
    onChange: (next: { label: string; value: string }) => void;
    options: AutocompleteOption[];
    disabled?: boolean;
    loading?: boolean;
    allowCustom?: boolean;
    onSelect?: (next: { label: string; value: string }) => void;
  }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState(value || '');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const skipOpenRef = useRef(false);
    useEffect(() => {
      setSearch(value || '');
    }, [value]);

    const filtered = useMemo(
      () =>
        options.filter((option) =>
          option.label.toLowerCase().includes(search.trim().toLowerCase()),
        ),
      [options, search],
    );

    const handleSelect = (option: AutocompleteOption) => {
      skipOpenRef.current = true;
      onChange({ label: option.label, value: option.value });
      onSelect?.({ label: option.label, value: option.value });
      setSearch(option.label);
      setOpen(false);
      inputRef.current?.blur();
    };

    const renderOption = (option: AutocompleteOption) => (
      <button
        type="button"
        key={option.value}
        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
        onClick={() => handleSelect(option)}
      >
        {option.label}
      </button>
    );

    const renderCustom = () => {
      const customLabel = search.trim();
      if (!allowCustom || !customLabel) return null;
      return (
        <button
          type="button"
          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
          onClick={() => {
            onChange({ label: customLabel, value: customLabel });
            onSelect?.({ label: customLabel, value: customLabel });
            setSearch(customLabel);
            setOpen(false);
          }}
        >
          Usar "{customLabel}"
        </button>
      );
    };

    return (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <div className="relative">
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={search}
            disabled={disabled}
            onFocus={() => {
              if (skipOpenRef.current) {
                skipOpenRef.current = false;
                return;
              }
              setOpen(true);
            }}
            onChange={(event) => {
              const text = event.target.value;
              setSearch(text);
              onChange({ label: text, value: text });
              setOpen(true);
            }}
            autoComplete="off"
            onBlur={() => {
              // cierra al salir del input sin cortar clics en el desplegable
              setTimeout(() => setOpen(false), 120);
            }}
            className="w-full"
          />
          {open && (
            <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white shadow-lg">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
              ) : null}
              {!loading && filtered.length === 0 ? (
                renderCustom() ?? (
                  <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
                )
              ) : (
                <>
                  {filtered.map(renderOption)}
                  {renderCustom()}
                </>
              )}
            </div>
          )}
        </div>
        <FormMessage />
      </FormItem>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen && !isSubmitting) onClose(); }}>
      <DialogContent className={dialogContentClassName} style={dialogContentStyle}>
        <DialogHeader>
          <DialogTitle>Editar item</DialogTitle>
          <DialogDescription>
            Completa los datos comerciales y elige la estancia donde debe aparecer.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="space-y-4 lg:[&_[data-slot=form-label]]:whitespace-nowrap"
            onSubmit={handleSubmit}
          >
            <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormField
                    control={form.control}
                    name="roomName"
                    rules={{ required: 'Indica la estancia a la que pertenece.' }}
                    render={({ field }) => (
                      <AutocompleteField
                        label="Estancia"
                        placeholder="Ej. Cocina principal"
                        value={field.value}
                        onChange={({ label }) => field.onChange(label)}
                        options={roomOptions}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                                    <FormField
                    control={form.control}
                    name="category"
                    rules={{
                      validate: () =>
                        form.getValues('categoryId') ? true : 'Selecciona una categoría válida.',
                    }}
                    render={({ field }) => (
                      <AutocompleteField
                        label="Categoría"
                        placeholder="Iluminación, mobiliario…"
                        value={field.value}
                        onChange={({ label }) => {
                          field.onChange(label);
                          form.setValue('categoryId', '');
                        }}
                        onSelect={({ label, value }) => {
                          field.onChange(label);
                          form.setValue('categoryId', value);
                        }}
                        options={categoryOptions}
                        disabled={isSubmitting}
                        loading={categoriesLoading}
                        allowCustom={false}
                      />
                    )}
                  />
                                    <FormField
                    control={form.control}
                    name="quantity"
                    rules={{ min: { value: 1, message: 'Mínimo 1 unidad.' } }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cantidad</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            step={1}
                            inputMode="numeric"
                            disabled={isSubmitting}
                            value={field.value}
                            onChange={(event) => field.onChange(Number(event.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estado</FormLabel>
                        <Select
                          disabled={isSubmitting}
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecciona el estado" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {stateOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
                                    <FormField
                    control={form.control}
                    name="ref"
                    rules={{ required: 'Introduce una referencia.' }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Referencia</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="REF-001"
                            disabled
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="supplier"
                    rules={{ required: 'Indica el proveedor.' }}
                    render={({ field }) => (
                      <AutocompleteField
                        label="Proveedor"
                        placeholder="Marca / distribuidor"
                        value={field.value}
                        onChange={({ label }) => {
                          field.onChange(label);
                          form.setValue('supplierId', '');
                        }}
                        onSelect={({ label, value }) => {
                          field.onChange(label);
                          form.setValue('supplierId', value);
                        }}
                        options={supplierOptions}
                        disabled
                        allowCustom={false}
                        loading={suppliersLoading}
                      />
                    )}
                  />

                </div>

                <FormField
                  control={form.control}
                  name="description"
                  rules={{ required: 'Describe el producto.' }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descripción</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Ej. Sofá modular de 3 plazas"
                          disabled
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormItem>
                    <FormLabel>PVP UD (€)</FormLabel>
                    <FormControl>
                      <Input value={pvpCalculated.toFixed(2)} readOnly disabled />
                    </FormControl>
                  </FormItem>
                                    <FormField
                    control={form.control}
                    name="discount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Descuento (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            inputMode="decimal"
                            disabled={isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormItem>
                    <FormLabel>PVP total (€)</FormLabel>
                    <FormControl>
                      <Input value={pvpTotalCalculated.toFixed(2)} readOnly disabled />
                    </FormControl>
                  </FormItem>
                  <FormItem>
                    <FormLabel>PVP IVA (€)</FormLabel>
                    <FormControl>
                      <Input value={pvpIvaCalculated.toFixed(2)} readOnly disabled />
                    </FormControl>
                  </FormItem>
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <FormField
                    control={form.control}
                    name="precio_tarifa"
                    rules={{ required: 'Introduce el precio tarifa.' }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Precio Tarifa (€)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            disabled
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="desvio_comercial_pct"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Desvío comercial (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            inputMode="decimal"
                            className="min-w-0"
                            disabled={isSubmitting || !desvioPersonalizadoEnabled}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="beneficio_comercial_pct"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Beneficio comercial (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            inputMode="decimal"
                            className="min-w-0"
                            disabled={isSubmitting || !beneficioPersonalizadoEnabled}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="montaje_pct"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Montaje (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            inputMode="decimal"
                            disabled={isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div aria-hidden />
                  <div className="mt-1 flex items-center gap-2">
                    <Checkbox
                      id="desvioPersonalizado"
                      checked={desvioPersonalizadoEnabled}
                      disabled={isSubmitting}
                      onCheckedChange={(checked) =>
                        form.setValue('desvio_personalizado', checked === true)
                      }
                    />
                    <label htmlFor="desvioPersonalizado" className="text-xs text-gray-600">
                      Personalizar
                    </label>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <Checkbox
                      id="beneficioPersonalizado"
                      checked={beneficioPersonalizadoEnabled}
                      disabled={isSubmitting}
                      onCheckedChange={(checked) =>
                        form.setValue('beneficio_personalizado', checked === true)
                      }
                    />
                    <label htmlFor="beneficioPersonalizado" className="text-xs text-gray-600">
                      Personalizar
                    </label>
                  </div>
                  <div aria-hidden />
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">


                  <FormItem>
                    <FormLabel>Coste UD (€)</FormLabel>
                    <FormControl>
                      <Input value={unitCostCalculated.toFixed(2)} readOnly disabled />
                    </FormControl>
                  </FormItem>
                                    <FormItem>
                    <FormLabel>Coste total (€)</FormLabel>
                    <FormControl>
                      <Input value={costTotalCalculated.toFixed(2)} readOnly disabled />
                    </FormControl>
                  </FormItem>
                                    <FormItem>
                    <FormLabel>Montaje (€)</FormLabel>
                    <FormControl>
                      <Input value={mountingAmountCalculated.toFixed(2)} readOnly disabled />
                    </FormControl>
                  </FormItem>
                                    <FormItem>
                    <FormLabel>Margen total (€)</FormLabel>
                    <FormControl>
                      <Input value={marginTotalCalculated.toFixed(2)} readOnly disabled />
                    </FormControl>
                  </FormItem>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="real_cost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Coste real (€)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            className="min-w-0"
                            disabled={!customRealBudgetEnabled || isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormItem>
                    <FormLabel>Desvío coste (€)</FormLabel>
                    <FormControl>
                      <Input value={desvioCosteCalculated.toFixed(2)} readOnly disabled />
                    </FormControl>
                  </FormItem>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
                  <div className="mt-1 flex items-center gap-2">
                    <Checkbox
                      id="customRealBudget"
                      checked={customRealBudgetEnabled}
                      disabled={isSubmitting}
                      onCheckedChange={(checked) =>
                        form.setValue('customRealBudget', checked === true)
                      }
                    />
                    <label htmlFor="customRealBudget" className="text-xs text-gray-600">
                      Personalizar
                    </label>
                  </div>
                  <div aria-hidden />
                </div>
              </div>

            {/* <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas internas</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder="Observaciones para el proveedor o el instalador" disabled={isSubmitting} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar cambios'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
